"use client";

import * as React from "react";
import { format, parse, isValid } from "date-fns";
import { CalendarIcon, X } from "lucide-react";

import { cn } from "@/utils/misc";
import { Calendar } from "@/ui/composed/data-display/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/composed/overlays/popover";

interface DatePickerProps {
  /** ISO date string (YYYY-MM-DD) */
  value?: string;
  /** Called with ISO date string (YYYY-MM-DD) */
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** Overrides the default input className. Use to match toolbar sizing. */
  inputClassName?: string;
}

/** Try to parse a user-typed string into a Date. Accepts DD/MM/YYYY, YYYY-MM-DD, D Mon YYYY. */
function parseUserInput(raw: string): Date | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  // YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const d = parse(trimmed, "yyyy-MM-dd", new Date());
    return isValid(d) ? d : null;
  }
  // DD/MM/YYYY
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(trimmed)) {
    const d = parse(trimmed, "d/M/yyyy", new Date());
    return isValid(d) ? d : null;
  }
  // D Mon YYYY or DD Mon YYYY (e.g. "31 Aug 2020", "1 Jan 2021")
  if (/^\d{1,2}\s+\w+\s+\d{4}$/.test(trimmed)) {
    const d = parse(trimmed, "d MMM yyyy", new Date());
    if (isValid(d)) return d;
    const d2 = parse(trimmed, "d MMMM yyyy", new Date());
    return isValid(d2) ? d2 : null;
  }

  return null;
}

/**
 * Date picker with both text input and popover calendar.
 * Accepts and emits ISO date strings (YYYY-MM-DD).
 */
export function DatePicker({
  value,
  onChange,
  placeholder = "Pick a date",
  disabled,
  className,
  inputClassName,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false);
  const [inputText, setInputText] = React.useState("");
  const [isFocused, setIsFocused] = React.useState(false);

  // Sync input text from value prop when not focused
  React.useEffect(() => {
    if (!isFocused) {
      if (value) {
        const d = parse(value, "yyyy-MM-dd", new Date());
        setInputText(isValid(d) ? format(d, "dd/MM/yyyy") : value);
      } else {
        setInputText("");
      }
    }
  }, [value, isFocused]);

  // Parse ISO string to Date for the calendar
  const selectedDate = value
    ? parse(value, "yyyy-MM-dd", new Date())
    : undefined;

  const handleSelect = (date: Date | undefined) => {
    if (date && onChange) {
      onChange(format(date, "yyyy-MM-dd"));
    }
    setOpen(false);
  };

  const handleInputBlur = () => {
    setIsFocused(false);
    if (!inputText.trim()) {
      if (value && onChange) onChange("");
      return;
    }
    const parsed = parseUserInput(inputText);
    if (parsed && onChange) {
      onChange(format(parsed, "yyyy-MM-dd"));
    } else if (value) {
      // Reset to current value if parse failed
      const d = parse(value, "yyyy-MM-dd", new Date());
      setInputText(isValid(d) ? format(d, "dd/MM/yyyy") : value);
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      (e.target as HTMLInputElement).blur();
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onChange) onChange("");
    setInputText("");
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <div className={cn("relative flex items-center", className)}>
        <input
          type="text"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={handleInputBlur}
          onKeyDown={handleInputKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            "h-10 w-full rounded-md border border-input bg-background py-2 pl-8 text-sm tabular-nums placeholder-muted-foreground ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
            // Only reserve room for the clear button when it is actually
            // rendered, so narrow pickers don't clip the placeholder.
            value ? "pr-7" : "pr-2",
            inputClassName,
          )}
        />
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground"
          >
            <CalendarIcon className="h-3.5 w-3.5" />
          </button>
        </PopoverTrigger>
        {value && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>
      <PopoverContent
        className="w-auto p-0"
        align="start"
        side="bottom"
        avoidCollisions
      >
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={handleSelect}
          defaultMonth={selectedDate}
        />
      </PopoverContent>
    </Popover>
  );
}
